import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  ButtonGroup,
  Switch,
  FormControlLabel,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import { SettingsSuggest as SettingsIcon, AccountBox as CustomerIcon } from '@mui/icons-material';
import GetRatesContext from '../../../../context/GetRates';
import { GetRatesContextType } from '../../Type';
import GetRatesAction from '../../GetRatesAction';
import Design1Form from './Design1Form';
import Design2Workspace from './Design2Workspace';
import MagicToolModal from './MagicToolModal';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useAccountLookup from '@Hook/useAccountLookup';
import SelectedUserInfoCard from '@Components/AccountLookup/SelectedUserInfoCard';
import AccountLookup from '@Components/AccountLookup';
import Divider from '../../../../components/Divider';

export default function AIAssistedGetRates() {
  const modalId = 'GetRatesV2Main';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedUser, setSelectedUser, resetSelectedUser } = useAccountLookup(modalId);

  const { setHideBoxes, setRateInput, setBoxes, setRatesInputHasError, setPickup, setDestination, setUserId, userId } =
    React.useContext(GetRatesContext) as GetRatesContextType;

  // AI Tab controls
  const [selectedDesign, setSelectedDesign] = useState<'design1' | 'design2'>('design1');
  const [isMagicModalOpen, setIsMagicModalOpen] = useState(false);
  const [aiFilledFields, setAiFilledFields] = useState<string[]>([]);
  const [isBoxDescBlocking, setIsBoxDescBlocking] = useState(false); // Can be configured as blocking/advisory

  // Track if overall validation has error
  const [validationError, setValidationError] = useState(true);

  // Check URL query parameters for prefill data from Chatbot
  useEffect(() => {
    const prefillData = searchParams.get('prefill');
    if (prefillData) {
      try {
        const decoded = JSON.parse(atob(prefillData));
        console.group('🤖 CHATBOT PREFILL APPLIED');
        console.info(decoded);
        console.groupEnd();

        // Update context states
        if (decoded.pickup) setPickup(decoded.pickup);
        if (decoded.destination) setDestination(decoded.destination);
        if (decoded.boxes) setBoxes(decoded.boxes);
        if (decoded.hideBoxes !== undefined) setHideBoxes(decoded.hideBoxes);

        setRateInput({
          shipmentType: decoded.shipmentType || 'parcel',
          pickup: decoded.pickup || {},
          destination: decoded.destination || {},
          pickupPinCode: decoded.pickupPinCode || '',
          destinationPinCode: decoded.destinationPinCode || '',
          weight: decoded.weight || 0,
          weightUnit: decoded.weightUnit || 'kg',
        });

        // Highlight fields filled by AI
        setAiFilledFields([
          'pickup',
          'destination',
          'pickupPinCode',
          'destinationPinCode',
          'shipmentType',
          'weight',
          'boxes',
        ]);

        // Customer specific pricing checks
        if (decoded.customerId || decoded.customerEmail) {
          // Setting User ID triggers contract rates logic
          // Note: In mock/real, we map email/ID to a customer id
          const mockUserId = decoded.customerId || '63bcd4bf3abcd13bf437829ba';
          setUserId(mockUserId);
          setSelectedUser({
            _id: mockUserId,
            name: 'AI Extracted Customer',
            email: decoded.customerEmail || 'customer@speedbox.com',
            accountNumber: 'ACC-AI-99',
          } as any);
        } else if (decoded.isTariffOnly) {
          setUserId('');
          resetSelectedUser();
        }

        // Clean the prefill param from the URL history
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('prefill');
        navigate(`?${newParams.toString()}`, { replace: true });
      } catch (err) {
        console.error('Failed to parse prefill URL data:', err);
      }
    }
  }, [searchParams]);

  // Synchronize validation errors to GetRatesContext so the Action component responds
  useEffect(() => {
    setRatesInputHasError(validationError);
  }, [validationError, setRatesInputHasError]);

  const handleConfirmExtraction = (extracted: any) => {
    if (extracted.pickup) setPickup(extracted.pickup);
    if (extracted.destination) setDestination(extracted.destination);
    if (extracted.boxes) setBoxes(extracted.boxes);
    if (extracted.hideBoxes !== undefined) setHideBoxes(extracted.hideBoxes);

    setRateInput({
      shipmentType: extracted.shipmentType || 'parcel',
      pickup: extracted.pickup || {},
      destination: extracted.destination || {},
      pickupPinCode: extracted.pickupPinCode || '',
      destinationPinCode: extracted.destinationPinCode || '',
      weight: extracted.weight || 0,
      weightUnit: extracted.weightUnit || 'kg',
    });

    if (extracted.aiFilledFields) {
      setAiFilledFields(extracted.aiFilledFields);
    }
  };

  const handleSelectedUser = (user: any) => {
    setUserId(user?._id || '');
  };

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      {/* 1. Control / Configuration Dashboard */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          backgroundColor: '#FAFAFA',
        }}
      >
        {/* Toggle Designs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="500">
            Design Approach:
          </Typography>
          <ButtonGroup variant="outlined" size="small" sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            <Button
              onClick={() => setSelectedDesign('design1')}
              variant={selectedDesign === 'design1' ? 'contained' : 'outlined'}
              sx={{ textTransform: 'none', px: 2, borderRadius: '20px 0 0 20px' }}
            >
              1. AI-Enhanced Form
            </Button>
            <Button
              onClick={() => setSelectedDesign('design2')}
              variant={selectedDesign === 'design2' ? 'contained' : 'outlined'}
              sx={{ textTransform: 'none', px: 2, borderRadius: '0 20px 20px 0' }}
            >
              2. Smart AI Workspace
            </Button>
          </ButtonGroup>
        </Box>

        {/* Validation Blocking Config Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <FormControlLabel
            control={
              <Switch checked={isBoxDescBlocking} onChange={e => setIsBoxDescBlocking(e.target.checked)} size="small" />
            }
            label={
              <Box>
                <Typography variant="caption" fontWeight="bold" display="block">
                  Box Descriptions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isBoxDescBlocking ? 'Blocking Rate calculation' : 'Advisory Only'}
                </Typography>
              </Box>
            }
          />
        </Box>
      </Paper>

      {/* 2. Customer Lookup Selection Card (AI Pricing Context) */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: '16px', border: '1px solid #EAEAEA' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={6}>
              <AccountLookup
                modalId={modalId}
                getSelectedUser={handleSelectedUser}
                showSearchInput
                disableUserReset={false}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CustomerIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    AI Pricing Context (Optional Customer)
                  </Typography>
                </Box>
              </AccountLookup>
              <Typography variant="caption" color="text.secondary">
                {userId
                  ? 'Calculating customer-specific contract rates.'
                  : 'No customer selected. Tariff (public) rates will be calculated.'}
              </Typography>
            </Grid>
          </Grid>
          {selectedUser?._id && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 2 }} />
              <SelectedUserInfoCard user={selectedUser} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 3. Render Selected Design Layout */}
      <Box sx={{ mb: 4 }}>
        {selectedDesign === 'design1' ? (
          <Design1Form
            onOpenMagicTool={() => setIsMagicModalOpen(true)}
            aiFilledFields={aiFilledFields}
            isBoxDescBlocking={isBoxDescBlocking}
            onValidationChange={setValidationError}
          />
        ) : (
          <Design2Workspace
            aiFilledFields={aiFilledFields}
            setAiFilledFields={setAiFilledFields}
            isBoxDescBlocking={isBoxDescBlocking}
            onValidationChange={setValidationError}
          />
        )}
      </Box>

      {/* 4. Action Buttons Footer */}
      <GetRatesAction isTarrifPricing={!userId} />

      {/* 5. Magic Tool Modal Dialog */}
      <MagicToolModal
        open={isMagicModalOpen}
        onClose={() => setIsMagicModalOpen(false)}
        onConfirmExtraction={handleConfirmExtraction}
      />
    </Box>
  );
}
